import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { nullableStringSetter } from '@/helpers';
import { Button, Input, Typography, Field, Form, Icon } from '@/components';
import { useUpdateOpportunity } from '@/features/projects/hooks/use-update-opportunity';
import { useUploadOpportunityFile } from '@/features/files/hooks';
import { sanitizeUrl } from '@/features/opportunities/helpers';
import { ASSETS_BASE_URL } from '@/config';

type UnderReviewProps = {
  opportunityId: string;
  initialVdrLink?: string | null;
  ndaFile?: any;
};

type UpdateOpportunityFields = {
  vdr_link?: string;
};

const ACCEPTED_FILE_TYPES = {
  'application/pdf': '.pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
};

export function UnderReview({ opportunityId, initialVdrLink, ndaFile }: UnderReviewProps) {
  const { t } = useTranslation(['offers']);
  const { onSubmitOpportunity } = useUpdateOpportunity({ opportunityId });
  const { uploadFile, isLoading } = useUploadOpportunityFile({ opportunityId });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const options = {
    defaultValues: {
      vdr_link: initialVdrLink ?? '',
    },
  };

  function handleSubmitVdrLink(data: UpdateOpportunityFields) {
    if (!data.vdr_link || data.vdr_link.trim() === '') {
      return;
    }

    const sanitizedUrl = sanitizeUrl(data.vdr_link);

    if (!sanitizedUrl && data.vdr_link) {
      return;
    }

    onSubmitOpportunity({ vdr_link: sanitizedUrl });
  }

  function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];

    if (!file) return;

    setFileError(null);

    const allowedTypes = Object.keys(ACCEPTED_FILE_TYPES);
    if (!allowedTypes.includes(file.type)) {
      setFileError(t('Only PDF and DOCX files are allowed'));
      setSelectedFile(null);
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setFileError(t('File size exceeds 10MB'));
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  }

  async function handleFileUpload() {
    if (!selectedFile) return;

    try {
      const res = await uploadFile(selectedFile);

      onSubmitOpportunity({ files: [{ file: res, type: 'nda' }] });

      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Upload error:', error);
      setFileError(t('Failed to upload file'));
    }
  }

  function handleRemoveFile() {
    setSelectedFile(null);
    setFileError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  function handleSelectClick() {
    if (!isLoading && !selectedFile) {
      fileInputRef.current?.click();
    }
  }

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  }

  return (
    <div className="bg-primary-100 mb-6 rounded-lg p-6">
      <div className="mb-4 flex cursor-pointer items-center justify-between" onClick={() => setIsExpanded(!isExpanded)}>
        <div>
          <Typography as="h3" weight="semibold" size="title">
            Your Project Is Under Review
          </Typography>
          <Typography as="p" className="mt-1">
            Our team is currently reviewing your submission to ensure all information and documentation meet marketplace
            standards. This process typically takes <strong>2-3 business days</strong>.
          </Typography>
        </div>
        <Icon
          name="chevron-down"
          className={`text-grey-500 h-5 w-5 transition-transform duration-300 ${isExpanded ? '' : 'rotate-180'}`}
        />
      </div>

      <div
        className={`grid overflow-hidden transition-all duration-300 ease-in-out ${
          isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="space-y-6">
            <div className="rounded-lg bg-white p-4 sm:p-5">
              <Typography as="h4" size="body-2" weight="semibold">
                What to Expect
              </Typography>
              <ul className="mt-2 mb-4 space-y-2">
                <li>
                  <Typography as="p" size="body-3">
                    • We're verifying the technical, legal, and financial details of your project.
                  </Typography>
                </li>
                <li>
                  <Typography as="p" size="body-3">
                    • Your teaser and deal materials are being refined for publication.
                  </Typography>
                </li>
                <li>
                  <Typography as="p" size="body-3">
                    • You may receive short requests for clarification or additional files.
                  </Typography>
                </li>
                <li>
                  <Typography as="p" size="body-3">
                    • During this stage, please <strong>select your plan below</strong> to confirm how you'd like your
                    project to be published.
                  </Typography>
                </li>
              </ul>

              <div className="border-grey-200 border-t pt-4">
                <Typography as="h4" size="body-2" weight="semibold">
                  💬 Need Help?
                </Typography>
                <Typography as="p" size="body-3" className="mt-1">
                  Your Deal Manager is here to guide you through this stage — from plan selection to document setup —
                  ensuring your deal is ready to attract investors and move forward quickly.
                </Typography>
              </div>
            </div>

            <div>
              <Typography as="h3" weight="semibold" size="title">
                Speed Up Your Deal
              </Typography>
              <Typography as="p" className="mt-1" size="body-3">
                Uploading the following documents now helps accelerate the process and gives buyers faster access once
                your project goes live.
              </Typography>

              <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="rounded-lg bg-white p-4 sm:p-5">
                  <Typography as="h4" size="body-2" weight="semibold">
                    Non-Disclosure Agreement (NDA)
                  </Typography>
                  <Typography as="p" size="body-3" className="mt-1">
                    Protect your confidential information before sharing detailed project data. You can upload your own
                    NDA or use nTeaser's standard version.
                  </Typography>
                  <Typography as="p" size="caption" className="mt-1">
                    Once your project is live, the NDA will be automatically shared with qualified investors when they
                    express interest.
                  </Typography>

                  <div className="mt-4 space-y-3">
                    {ndaFile ? (
                      <div>
                        <a
                          href={`${ASSETS_BASE_URL}/${ndaFile?.file?.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary-700 flex items-center hover:underline"
                        >
                          {ndaFile?.file?.title || ndaFile?.file?.filename_download || 'Untitled'}
                          <Icon name="external-link" className="ml-1 h-5 w-5" />
                        </a>
                      </div>
                    ) : (
                      <>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          onChange={handleFileSelect}
                          className="hidden"
                          disabled={isLoading}
                        />

                        {selectedFile && (
                          <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3">
                            <div className="min-w-0 flex-1">
                              <Typography as="p" size="body-3" weight="semibold" className="truncate">
                                {selectedFile.name}
                              </Typography>
                              <Typography as="p" size="caption" className="text-gray-500">
                                {formatFileSize(selectedFile.size)}
                              </Typography>
                            </div>
                            <Button color="secondary" icon="trash" onClick={handleRemoveFile}></Button>
                          </div>
                        )}

                        {fileError && (
                          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                            <Typography as="p" size="body-3" className="text-red-600">
                              {fileError}
                            </Typography>
                          </div>
                        )}

                        {!selectedFile ? (
                          <Button
                            onClick={handleSelectClick}
                            disabled={isLoading}
                            size="sm"
                            color="primary"
                            className="mt-3 w-full"
                          >
                            {t('Select NDA File')}
                          </Button>
                        ) : (
                          <Button
                            onClick={handleFileUpload}
                            disabled={isLoading}
                            type="submit"
                            size="sm"
                            color="primary"
                            className="mt-3 w-full"
                          >
                            {isLoading ? t('Uploading...') : t('Upload NDA')}
                          </Button>
                        )}

                        <p className="text-muted-foreground mt-2 text-xs">Format: PDF or DOCX, max 10MB</p>
                      </>
                    )}
                  </div>
                </div>

                <div className="rounded-lg bg-white p-4 sm:p-5">
                  <Typography as="h4" size="body-2" weight="semibold">
                    Virtual Data Room (VDR) Link
                  </Typography>
                  <Typography as="p" size="body-3" className="mt-1">
                    Provide secure access to your project documentation for verified buyers.
                  </Typography>
                  <Typography as="p" size="caption" className="mt-1">
                    Your VDR will only be shared with investors after they've signed the NDA. If you don't have one yet,
                    your Deal Manager can help you set it up quickly.
                  </Typography>
                  <div className="mt-4 space-y-2">
                    <Form<UpdateOpportunityFields> id="form-vdr-link" onSubmit={handleSubmitVdrLink} options={options}>
                      {({ formState, register, watch }) => {
                        const vdrLinkValue = watch('vdr_link');
                        const isVdrLinkEmpty = !vdrLinkValue || vdrLinkValue.trim() === '';

                        return (
                          <>
                            <Field
                              id="vdr_link"
                              error={formState.errors['vdr_link']}
                              delay={0}
                              classes={{ field: 'space-y-1' }}
                            >
                              <Input
                                id="vdr_link"
                                placeholder="https://your-vdr-link.com"
                                color={formState.errors['vdr_link'] ? 'error' : 'primary'}
                                autoComplete="off"
                                registration={register('vdr_link', {
                                  setValueAs: nullableStringSetter,
                                  validate: (value) =>
                                    !value ||
                                    /^https:\/\/([\w-]+\.)+[\w-]{2,}(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/.test(value) ||
                                    t('Enter a valid URL'),
                                })}
                              />
                            </Field>

                            <Button
                              type="submit"
                              size="sm"
                              color="primary"
                              className="mt-3 w-full"
                              disabled={isVdrLinkEmpty}
                            >
                              {t('Add VDR Link')}
                            </Button>
                          </>
                        );
                      }}
                    </Form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
